import React from 'react';
import { reaction, when } from 'mobx';
import Modal from '@tencent/dui/lib/components/Modal';
import Snackbar from '@tencent/dui/lib/components/Snackbar';
import i18n from '@i18next/i18n';
import { DrawerService, InjectorService, EventEmitter } from '@tencent/workbench';
import { DrawerName } from '@src/framework/workbench/drawer/constants';
import { SearchImageInfo } from '@src/slide/application/service/ai/core/api/api.interface';
import { IPictureModel } from '@src/slide/domain/ui-model/element-modeles/Interface/picture-model.interface';
import {
    ISmartReplaceImageService,
    SmartReplaceDelegateServices,
} from '@src/slide/application/service/smart-replace-image/smart-replace-image-service.interface';
import { AiReplaceImageContent } from '@src/slide/application/service/smart-replace-image/replace-dialog/dialog-content';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';
import { ReplaceImageDrawer } from './replace-drawer/replace-image-drawer';
import { ReplaceImageDrawerTitle } from './replace-drawer/drawer-title';
import { IDrawerStateManager } from '../drawer-state-manager/core/drawer-state-manager.interface';
import { ReplaceGenerateCard } from './replace-card/replace-card';
import { SelectedModelType } from '../selection/selection-service.interface';
import { getQuickSettingRect } from '@src/framework/frame/components/quick-setting/quick-setting-rect';
import { ReplaceImageUtils } from './utils/replace-image-utils';

const GENERATE_CARD_WIDTH = 328;

/**
 * 管理所有智能替换图片的功能，目前包括：
 * 1. 批量替换 ai 生成图片弹窗
 * 2. 右侧栏搜索替换图片模块
 * 3. 重新生成图片弹窗
 *
 * SmartReplaceImageService 按需加载，在 workbench 加载好后加载即可。也许需要一个初始化时机，在 workbench 加载的时候调用
 * TODO：所有内容是否需要异步加载
 */
export class SmartReplaceImageService implements ISmartReplaceImageService {
    private drawerService: DrawerService;
    private eventEmitter: EventEmitter;
    private drawerStateManager?: IDrawerStateManager;
    private services: SmartReplaceDelegateServices;
    private generateCardInstance?: { close?: () => void }; // 生图弹窗实例
    private closeCardUnSubscribe?: () => void; // 生图弹窗关闭监听器

    public constructor() {
        this.drawerService = InjectorService.get(DrawerService);
        this.eventEmitter = InjectorService.get(EventEmitter);
    }

    public setDelegateService(services: SmartReplaceDelegateServices) {
        this.services = { ...services };
        reaction(
            () => this.services.rightSideBarManager.isReplaceImageShown,
            (show) => {
                if (show) {
                    this.showReplaceImageDrawer();
                    this.services.aiService.dangerousInstance?.hideSideBar();
                } else {
                    this.closeReplaceImageDrawer();
                }
                window.dispatchEvent(new Event('resize'));
            },
        );
    }

    // 展示批量替换图片弹窗
    public async showBulkReplaceDialog(images: SearchImageInfo[], pageIndex = 0) {
        if ((ReplaceImageUtils.getAllAiPictures().length ?? 0) <= 0) {
            Snackbar.show({ message: i18n.t('很抱歉，文档中没有可替换的AI图片'), autoClose: true, type: 'warning' });
            return;
        }
        return new Promise<void>((resolve) => {
            const { close } = Modal.confirm({
                title: (
                    <div style={{ margin: '21px 0 21px 0', paddingLeft: 24, lineHeight: '22px' }}>
                        {i18n.t('批量图片替换')}
                    </div>
                ),
                style: { padding: 0, width: 1000 },
                content: (
                    <AiReplaceImageContent
                        imagesToReplace={images}
                        pageIndex={pageIndex}
                        close={() => {
                            resolve();
                            close();
                        }}
                        getPictureModelById={this.getPictureModelById}
                    />
                ),
                footer: null,
                onClose: () => resolve(),
            });
        });
    }

    // 展示重新生成图片弹窗
    public showGenerateCard(domId?: string, model?: SelectedModelType) {
        if (!domId || !model) {
            (window as any).logger?.error('no quick-setting dom or selected-model');
            return;
        }
        const button = document.getElementById(domId);
        const buttonRect = button?.getBoundingClientRect();
        const { x, y } = this.getGenerateCardPosition(model);
        const style = {
            position: 'absolute' as const,
            top: y ?? 12 + (buttonRect?.bottom ?? 0),
            left: x ?? buttonRect?.left ?? 0,
            width: GENERATE_CARD_WIDTH,
            padding: 0,
            borderRadius: 4,
            overflow: 'hidden',
        };
        this.closeGenerateCard();
        this.generateCardInstance = Modal.confirm({
            mask: false,
            draggable: true,
            style,
            closable: false,
            title: null,
            content: <ReplaceGenerateCard selectedModel={model} onClose={() => this.generateCardInstance?.close?.()} />,
            footer: null,
        });
        // 自动关闭卡片
        this.closeCardUnSubscribe = model.sdkData?.subscribe((change) => {
            if (change?.diffs?.data?.nvPr?.descr !== undefined) {
                this.closeGenerateCard();
            }
        });
        when(
            () =>
                [
                    this.services.selectionService.currentSlide !== model.slideRef,
                    (model.parent?.children.findIndex(i => i.id === model.id) ?? -1) < 0,
                ].includes(true),
            () => this.closeGenerateCard(),
        );
    }

    public closeGenerateCard() {
        this.generateCardInstance?.close?.();
        this.closeCardUnSubscribe?.();
    }

    // 展示联网搜索图片侧边栏
    public showReplaceDrawer() {
        this.services.rightSideBarManager.setReplaceImageShown(true);
        this.eventEmitter.emit('slide-replace-image-keyword-change', this.getSearchKeyword());
    }

    private showReplaceImageDrawer() {
        this.drawerService.showRightDrawer({
            name: DrawerName.REPLACE_IMAGE,
            title: <ReplaceImageDrawerTitle />,
            content: (
                <ReplaceImageDrawer
                    selectionService={this.services.selectionService}
                    eventEmitter={this.eventEmitter}
                    defaultKeyword={this.getSearchKeyword()}
                />
            ),
            style: { display: 'flex', flexDirection: 'column' },
            onClose: () => this.services.rightSideBarManager.setReplaceImageShown(false),
        });
        this.services.drawerStateManager.promise().then(manager => manager.handleDrawerHolderVisiable(true));
    }

    private closeReplaceImageDrawer() {
        this.services.drawerStateManager.promise().then(manager => manager.handleDrawerHolderVisiable(false));
    }

    // 获取默认的搜索关键词
    private getSearchKeyword(): string {
        const model = this.services.selectionService.getSelectedModel();
        const titleList = ReplaceImageUtils.getPageTitleList(model?.slideRef ?? undefined);
        const titleKeyword = titleList.reverse().reduce((pre, cur) => (pre ? pre : cur), '');
        // 搜索出的图片不提供关键词
        if (!model?.sdkData?.props?.nvPr?.descr || model.sdkData?.props?.nvPr?.descr?.includes('sourceUrl')) {
            return '';
        }
        return this.services.selectionService.getSelectedModel()?.sdkData?.props?.nvPr?.name ?? titleKeyword;
    }

    // 由 id 获取图片的 uiModel
    private getPictureModelById = (slideId: string, shapeId: string): IPictureModel | undefined => {
        const { uiDataService } = this.services;
        const model = uiDataService?.presentation.findElementWithId(slideId, shapeId);
        if (model?.type !== UIModelType.IMAGE) {
            return;
        }
        // 获取 model 时切换选取
        if (model.slideRef?.slideIndex) {
            this.services.selectionService.clearAll();
            this.services.selectionService.currentSlideIndex = model.slideRef.slideIndex;
        }
        return model as IPictureModel;
    };

    // 计算重新生成图片弹窗的显示位置
    private getGenerateCardPosition(model: NonNullable<SelectedModelType>) {
        const vw = document.body.clientWidth;
        const scale = this.services.editAreaCanvasSizeManager.getCanvasScale();
        const defaultQuickSettingRect = getQuickSettingRect(model, scale, false);
        const { x = 0, y = 0, width = 0 } = defaultQuickSettingRect ?? {};

        let positionX: number | undefined = x + width + 8;
        if (positionX + GENERATE_CARD_WIDTH > vw) {
            positionX = x - GENERATE_CARD_WIDTH - 8;
        }
        if (positionX < 0) {
            positionX = undefined;
        }
        return { x: positionX, y };
    }
}
